// Copyright (c) StrangeBee 2023

import { DashboardDropRowResult, WIDGET_DND_TYPES } from '@Types';
import React, { ReactElement, useRef, ReactNode } from 'react';
import { useDrop, useDrag } from 'react-dnd';
import EmptyElement from '@Organisms/Dashboard/DashboardDetails/EmptyElementToDrop/EmptyElementToDrop';
import { useLocalMessage } from '@Helpers/';

interface DropableWidgetRowProps {
    children: ReactNode;
    rowIndex: number;
    moveRow: (dropResult: DashboardDropRowResult) => void;
}

const DropableWidgetRow = ({ children, rowIndex, moveRow }: DropableWidgetRowProps): ReactElement => {
    const i = useLocalMessage();
    const ref = useRef(null);

    const [{ isHovered }, drop] = useDrop({
        accept: WIDGET_DND_TYPES.ROW,
        collect: (monitor) => ({
            isHovered: monitor.isOver(),
        }),
        drop: () => {
            return { hoveredRowIndex: rowIndex };
        },
    });

    const [{ isDragging }, drag] = useDrag({
        item: { type: WIDGET_DND_TYPES.ROW, draggedRowIndex: rowIndex },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        end: (item, monitor) => {
            const dropResult = monitor.getDropResult();

            if (dropResult) {
                moveRow?.(dropResult);
            }
        },
    });

    drag(drop(ref));

    return (
        <div ref={ref}>
            {!isDragging && (
                <>
                    {isHovered && <EmptyElement text={i('dashboard.drop.row')} />}
                    {children}
                </>
            )}
        </div>
    );
};

export default DropableWidgetRow;
